<template>
    <AdminLayout v-bind="appSharedData.getAdminLayoutProps(pageMeta)">
        <div class="space-y-4">
            <div class="grid grid-cols-10 gap-4">
                <StatCard
                    title="Jobs per minute"
                    :currentValue="localStats?.jobsExecutedPerMinuteCount"
                    :previousValue="
                        previousLocalStats?.jobsExecutedPerMinuteCount
                    "
                />
                <StatCard
                    title="Completed jobs last hour"
                    :currentValue="localStats?.completedJobsInTheLastHourCount"
                    :previousValue="
                        previousLocalStats?.completedJobsInTheLastHourCount
                    "
                />
                <StatCard
                    title="Total failed jobs"
                    :currentValue="localStats?.totalFailedJobsCount"
                    :previousValue="previousLocalStats?.totalFailedJobsCount"
                />
                <StatCard
                    title="Failed jobs last 24 hours"
                    :currentValue="localStats?.failedJobsInTheLastDayCount"
                    :previousValue="
                        previousLocalStats?.failedJobsInTheLastDayCount
                    "
                />
                <StatCard
                    title="Pending jobs"
                    :currentValue="localStats?.pendingJobsCount"
                    :previousValue="previousLocalStats?.pendingJobsCount"
                />
            </div>

            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-12 lg:col-span-3">
                    <form
                        :action="RoutePathAppEnum.Admin_Queue_Pending"
                        method="get"
                        ref="formRef"
                    >
                        <div
                            class="flex flex-col lg:flex-row items-center justify-start gap-2"
                        >
                            <FormControl label="Queue">
                                <SelectInput
                                    :name="
                                        RequestQueryKeyEnum.Admin_Queue_QueueName
                                    "
                                    :value="searchFilters.queueName"
                                    @change="submitForm()"
                                >
                                    <option value="All">All</option>
                                    <template
                                        v-for="queueName in queueNames"
                                        :key="queueName"
                                    >
                                        <option :value="queueName">
                                            {{ queueName }}
                                        </option>
                                    </template>
                                </SelectInput>
                            </FormControl>

                            <FormControl label="Job State">
                                <SelectInput
                                    :name="
                                        RequestQueryKeyEnum.Admin_Queue_JobState
                                    "
                                    :value="searchFilters.jobState"
                                    @change="submitForm()"
                                >
                                    <option value="All">All</option>
                                    <template
                                        v-for="jobState in jobStates"
                                        :key="jobState"
                                    >
                                        <option :value="jobState">
                                            {{ jobState }}
                                        </option>
                                    </template>
                                </SelectInput>
                            </FormControl>
                        </div>
                    </form>
                </div>
            </div>

            <PendingQueueJobsTable
                :pendingJobs="localPaginatedPendingJobs.records"
            />

            <div class="flex justify-center">
                <Pagination
                    :searchResults="localPaginatedPendingJobs"
                    :url="paginationBaseUrl"
                />
            </div>
        </div>

        <ApiForm
            :url="fetchStatsUrl"
            method="GET"
            ref="apiFormRef"
        />
    </AdminLayout>
</template>

<script setup lang="ts">
    import { ref, onMounted } from 'vue'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import AdminLayout from '@/Shared/Layout/Admin/AdminLayout.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import Pagination from '@/Shared/Component/Pagination.vue'
    import PendingQueueJobsTable from '@/Admin/Queue/Component/PendingQueueJobsTable.vue'
    import StatCard from '@/Admin/Queue/Component/StatCard.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import {
        ResponseJsonKeyEnum,
        type PaginatedResultsType,
        type QueueJobSearchFiltersType,
        type QueueStatsDataType,
        type PageMeta,
    } from '@/Shared/AutoGenerated/Main'
    import ApiFormResponseType from '@/Shared/Component/Forms/ApiFormResponseType'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import { RoutePathAppEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
    import { RequestQueryKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface IndexPendingQueueJobsProps {
        pageMeta: PageMeta
        appSharedDataData: any
        queueNames: string[]
        jobStates: string[]
        searchFilters: QueueJobSearchFiltersType
        paginatedJobs: PaginatedResultsType
        pendingQueueStats: QueueStatsDataType
    }

    const props = defineProps<IndexPendingQueueJobsProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const apiFormRef = ref<ApiFormRefType>(null)
    const previousLocalStats = ref(props.pendingQueueStats)
    const localStats = ref<QueueStatsDataType>(props.pendingQueueStats)
    const localPaginatedPendingJobs = ref<PaginatedResultsType>(
        props.paginatedJobs,
    )

    const fetchStatsUrl = new UrlFactory().makeAdminQueuePendingStatsUrl(
        props.searchFilters.jobState,
        props.searchFilters.queueName,
    )

    const paginationBaseUrl = new UrlFactory().makeAdminQueuePendingPageUrl(
        props.searchFilters.jobState,
        props.searchFilters.queueName,
    )

    onMounted(() => {
        setInterval(() => {
            apiFormRef.value
                ?.submit()
                ?.then((response: ApiFormResponseType) => {
                    previousLocalStats.value = localStats.value
                    localStats.value = response.getHydratedQueueStats(
                        ResponseJsonKeyEnum.Admin_Queue_Pending_Stats,
                    )
                    localPaginatedPendingJobs.value =
                        response.getHydratedPaginatedResults(
                            ResponseJsonKeyEnum.Admin_Queue_Pending_PaginatedJobs,
                        )
                })
        }, 5000)
    })

    const formRef = ref(null)

    const submitForm = () => {
        // @ts-ignore
        formRef.value?.submit()
    }
</script>
